import { useCallback, useContext, useEffect, useState } from 'react'
import { getReport } from '../../services/api'
import styles from './Reports.module.css'
import sidebarStyles from '../bets/sidebar/Sidebar.module.css'
import Loader from 'react-spinners/BounceLoader'
import { ReportBetCard } from './views/ReportBetCard'
import { ReportSummary } from './views/ReportSummary'
import {
    getDisplayEndDate,
    ReportPeriod,
    ReportType,
    setPeriodRange,
    SummaryType,
} from './helpers'
import { ReportOverview } from './views/ReportOverview'
import { ReportFilters } from './views/ReportFilters'
import { ReportDownloadModal } from './views/ReportDownloadModal'
import { BetIF } from '../bets/types'
import { ReportIF, ReportFilterIF } from './types'
import { Context } from '../../utils/context'
import { DateFilterModal } from './views/DateFilterModal'
import { UTCToEST } from '../../utils/timezone'

export const Reports = () => {
    const { loggedInUser } = useContext(Context)

    const [period, setPeriod] = useState<ReportPeriod>(
        setPeriodRange(
            ReportType.TODAY
        )
    )
    const [report, setReport] = useState<ReportIF>()
    const [selectedFilters, setSelectedFilters] = useState<ReportFilterIF>({
        status: [],
        account: [],
        outcome: [],
        user: [],
        provider: [],
        source: [],
        error: [],
        graded: []
    })
    const [showSummary, setShowSummary] = useState<SummaryType | undefined>(
        undefined
    )
    const [page, setPage] = useState<number | null>(1)
    const [isLoading, setIsLoading] = useState(false)
    const [showDownloadModal, setShowDownloadModal] = useState(false)
    const [showDateFilterModal, setShowDateFilterModal] = useState<boolean>(false)


    useEffect(() => {
        setPage(1)
    }, [period, selectedFilters])

    useEffect(() => {
        if (!page) return;
        const abortController = new AbortController()
            ; (async () => {
                setIsLoading(true);
                const res = await getReport(
                    period.start,
                    period.end,
                    selectedFilters,
                    page
                )
                if (!res?.bets || res.bets.length === 0) {
                    setPage(null)
                    setIsLoading(false)
                    return
                }
                res &&
                    setReport((prev) => ({
                        totals: res.totals,
                        bets: [
                            ...(page > 1 ? prev?.bets ?? [] : []),
                            ...res.bets,
                        ],
                        filters: res.filters ?? prev?.filters ?? null,
                    }))
                setIsLoading(false)
            })()
        return () => abortController.abort()
    }, [period, selectedFilters, page])

    const showBets = useCallback((bets: BetIF[]) => {
        const betsDict = bets.reduce(
            (acc: { [groupId: string]: BetIF[] }, bet: BetIF) => {
                if (!acc[bet.groupId]) {
                    acc[bet.groupId] = []
                }
                acc[bet.groupId].push(bet)

                return acc
            },
            {}
        )

        return Object.values(betsDict)
            .sort((a, b) =>
                String(b[0].createdAt).localeCompare(String(a[0].createdAt))
            )
            .map((bets) => <ReportBetCard key={bets[0].id} bets={bets} />)
    }, [])

    return (
        <div className={styles.content}>
            <div className={sidebarStyles.sidebar}>
                <header>Overview</header>
                <ReportOverview
                    overview={report?.totals}
                    setShowDownloadModal={setShowDownloadModal}
                    role={loggedInUser?.role}
                />
            </div>
            <div className={styles.reports}>
                <div className={styles.header}>
                    <header>{`Report for ${period.type} ${period.type === ReportType.TODAY || period.type === ReportType.YESTERDAY ? `(${(UTCToEST(period.start))})` : `(${UTCToEST(period.start)}-${UTCToEST(getDisplayEndDate(period.end))})`}`}</header>
                    <button onMouseDown={() => setShowDateFilterModal(true)}>{period.type}</button>
                </div>
                <div className={styles.container}>
                    <div className={styles.header}>
                        {!showSummary
                            ? 'Bets'
                            : showSummary === SummaryType.ACCOUNT
                                ? 'Account Summary'
                                : showSummary === SummaryType.SOURCE
                                    ? 'Agent Summary'
                                    : ''}
                        {report && report.filters && (
                            <ReportFilters
                                filters={report.filters}
                                selectedFilters={selectedFilters}
                                setSelectedFilters={setSelectedFilters}
                                showSummary={showSummary}
                                setShowSummary={setShowSummary}
                                dropdown
                            />
                        )}
                    </div>
                    <div className={styles.bet_card_container}>
                        <div className={styles.filter_buttons}>
                            {report && report.filters && !showSummary && (
                                <ReportFilters
                                    filters={report.filters}
                                    selectedFilters={selectedFilters}
                                    setSelectedFilters={setSelectedFilters}
                                    showSummary={showSummary}
                                    setShowSummary={setShowSummary}
                                />
                            )}
                        </div>
                        {page === 1 && isLoading ? (
                            <div className={styles.loading}>
                                <Loader
                                    loading={isLoading}
                                    size={48}
                                    color={'var(--dark1)'}
                                />
                            </div>
                        ) : report && report.bets.length > 0 ? (
                            <div className={styles.scrollable}>
                                {showSummary === SummaryType.ACCOUNT ? (
                                    <>
                                        <ReportSummary
                                            type="Account"
                                            startDate={period.start}
                                            endDate={period.end}
                                            source={selectedFilters.source[0]}
                                            onClick={(account: string) => {
                                                setShowSummary(undefined)
                                                setSelectedFilters({
                                                    provider: [],
                                                    status: [],
                                                    outcome: [],
                                                    user: [],
                                                    source: [],
                                                    account: [account],
                                                    error: [],
                                                    graded: []
                                                })
                                            }}
                                        />
                                    </>
                                ) : showSummary === SummaryType.SOURCE ? (
                                    <>
                                        <ReportSummary
                                            type="Source"
                                            startDate={period.start}
                                            endDate={period.end}
                                            onClick={(source: string) => {
                                                setShowSummary(undefined)
                                                setSelectedFilters({
                                                    provider: [],
                                                    status: [],
                                                    outcome: [],
                                                    user: [],
                                                    source: [source],
                                                    account: [],
                                                    error: [],
                                                    graded: []
                                                })
                                            }}
                                        />
                                    </>
                                ) : (
                                    report.bets && showBets(report.bets)
                                )}
                                {!report && isLoading ? (
                                    <div className={styles.loading}>
                                        <Loader
                                            loading={isLoading}
                                            size={24}
                                            color={'var(--dark2)'}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        {!showSummary && page && (
                                            <p
                                                className={styles.show_more}
                                                onClick={() =>
                                                    setPage(page + 1)
                                                }
                                            >
                                                Show More
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : (
                            <p style={{ padding: '0.5rem 1rem' }}>
                                No bets to show.
                            </p>
                        )}
                    </div>
                </div>
            </div>
            {
                showDownloadModal && (
                    <ReportDownloadModal
                        period={period}
                        filters={report?.filters}
                        selectedFilters={selectedFilters}
                        onClose={() => setShowDownloadModal(false)}
                    />
                )
            }
            {
                showDateFilterModal &&
                <DateFilterModal initialPeriod={period.type} setDateFilterModal={setShowDateFilterModal} setPeriod={setPeriod} />
            }
        </div >
    )
}
